import {
  ShieldCheckIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  CurrencyEuroIcon,
  ChatBubbleLeftRightIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import NavBar from '../features/navbar/Navbar';
import Footer from '../features/common/Footer';
import MainLayout from '../components/MainLayout';
import { useLanguage } from '../context/LanguageContext';
import { useRefundPolicyTranslations } from '../context/refundPolicyTranslations';

function RefundPolicy() {
  const { language } = useLanguage();
  const t = useRefundPolicyTranslations(language);

  const warranties = t.warranties.map((warranty, index) => {
    const icons = [CalendarDaysIcon, ShieldCheckIcon];
    const colors = ['from-blue-500 to-blue-700', 'from-green-500 to-green-700'];
    return {
      ...warranty,
      icon: icons[index],
      color: colors[index],
    };
  });

  const refundProcess = t.refundProcess;

  const eligibilityItems = t.eligibilityItems;

  return (
    <MainLayout>
      <NavBar>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-14 sm:py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 text-white mb-6">
              <DocumentTextIcon className="w-8 h-8" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
              {t.pageTitle}
            </h1>
            <p className="text-base sm:text-lg text-blue-100 max-w-3xl mx-auto">
              {t.pageSubtitle}
            </p>
          </div>
        </div>

        {/* Warranty Cards Section */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-10 text-center flex items-center justify-center gap-3">
            <ShieldCheckIcon className="w-8 h-8 text-blue-600" />
            {t.warrantyCoverage}
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {warranties.map((warranty) => {
              const IconComponent = warranty.icon;
              return (
                <div
                  key={warranty.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  <div className={`bg-gradient-to-r ${warranty.color} p-6 text-white`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white/20">
                          <IconComponent className="h-7 w-7" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">{warranty.title}</h3>
                          <p className="text-white/80 text-sm">{t.replacementWarranty}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold">{warranty.duration}</p>
                        <p className="text-white/80 text-xs uppercase">{t.warranty}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-gray-700 font-medium mb-3">{warranty.description}</p>
                    <p className="text-gray-600 text-sm">{warranty.details}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Refund Process Section */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-10">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-10">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-10 text-center flex items-center justify-center gap-3">
              <ArrowPathIcon className="w-7 h-7 text-blue-600" />
              {t.refundProcessTitle}
            </h2>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {refundProcess.map((item, idx) => (
                <div key={idx} className="text-center relative">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Refund Calculation Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-10">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 mb-6 flex items-center gap-3">
              <CurrencyEuroIcon className="w-8 h-8 text-blue-600" />
              {t.refundCalculation}
            </h2>

            <div className="space-y-4">
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>{t.fullRefund}</strong> {t.fullRefundDesc}
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">{t.proRataTitle}</h4>
                <p className="text-gray-600 text-sm mb-4">
                  {t.proRataDesc}
                </p>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{t.proRataExample}</span> {t.proRataExampleText}
                  </p>
                </div>
                <p className="text-gray-500 text-xs mt-3">
                  {t.proRataNote}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Eligibility Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-10">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 mb-6 flex items-center gap-3">
              <InformationCircleIcon className="w-8 h-8 text-blue-600" />
              {t.refundEligibility}
            </h2>

            <div className="space-y-3">
              {eligibilityItems.map((item, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-4 rounded-lg ${
                    item.eligible ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}
                >
                  {item.eligible ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  )}
                  <p className={`text-sm ${item.eligible ? 'text-green-800' : 'text-red-800'}`}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm flex items-start gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{t.importantNote}</strong> {t.importantNoteText}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Processing Time Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            </div>
            <div className="relative z-10">
              <div className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 text-white flex-shrink-0">
                  <ClockIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-3">
                    {t.processingTime}
                  </h2>
                  <p className="text-blue-100 leading-relaxed mb-6">
                    {t.processingTimeDesc} <strong className="text-white">{t.businessDays}</strong> {t.processingTimeDescCont}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3">
                      <CheckCircleIcon className="h-5 w-5 text-white" />
                      <span className="text-white text-sm font-medium">
                        {t.processingBadge}
                      </span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3">
                      <ShieldCheckIcon className="h-5 w-5 text-white" />
                      <span className="text-white text-sm font-medium">
                        {t.secureRefund}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Exclusions Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 sm:p-10">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 mb-6 flex items-center gap-3">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
              {t.refundExclusions}
            </h2>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">
                  {t.exclusion1} <strong>{t.exclusion1Not}</strong> {t.exclusion1Text}
                </p>
              </li>
              <li className="flex items-start gap-3">
                <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">
                  {t.exclusion2} <strong>{t.exclusion2Not}</strong> {t.exclusion2Text}
                </p>
              </li>
            </ul>
          </div>
        </div>

        {/* Support Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-10 text-center">
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-green-500 text-white">
                <ChatBubbleLeftRightIcon className="h-7 w-7" />
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 mb-4">
              {t.needAssistance}
            </h2>
            <p className="text-gray-600 mb-6">
              {t.supportDesc}
            </p>

            <a
              href="https://wa.me/923272341839"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-200 text-lg mb-6"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t.contactSupport}
            </a>

            <p className="text-gray-500 text-sm">
              👉 <a href="https://wa.me/923272341839" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline font-medium">
                https://wa.me/923272341839
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </NavBar>
    </MainLayout>
  );
}

export default RefundPolicy;
